import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { YAxis } from "recharts";
import type { AxisDomain } from "recharts/types/util/types";

import { MeasurableValueAxisTick } from "@/components/ui/columnChartValueAxisTicks";
import {
  getNumericValuesFromChartData,
  getYAxisDomain,
  getYAxisDomainForNumericValues,
} from "@/lib/utils/chartUtils";
import { addCommasToNumbers } from "@/lib/utils/prettyName";

const DEFAULT_VALUE_AXIS_WIDTH_PX = 56;

export const CHART_VALUE_AXIS_TOP_MARGIN_PX = 10;

/** Comma-separated numeric tick labels for column-chart value axes. */
export const formatChartValueAxisTick = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) return "0";
  return addCommasToNumbers(value);
};

export interface UseColumnChartValueAxisOptions {
  /** When false, domain starts at 0 — use for bar/column charts. @default true */
  autoMinValue?: boolean;
  /** When set, domain floors at 0 unless any series value is negative. */
  valueKeys?: string[];
}

/** Measures label width and returns props for {@link ColumnChartValueYAxis}. */
export const useColumnChartValueAxis = (
  data: unknown[],
  { autoMinValue = true, valueKeys }: UseColumnChartValueAxisOptions = {},
) => {
  const [calculatedYAxisWidth, setCalculatedYAxisWidth] = useState(
    DEFAULT_VALUE_AXIS_WIDTH_PX,
  );
  const [maxYAxisLabelWidth, setMaxYAxisLabelWidth] = useState(0);
  const numericValues = useMemo(
    () =>
      valueKeys != null && valueKeys.length > 0
        ? getNumericValuesFromChartData(data, valueKeys)
        : null,
    [data, valueKeys],
  );
  const seriesValuesSignature =
    numericValues != null ? numericValues.join(",") : "";
  const dataSignature = `${data.length}:${seriesValuesSignature}`;
  const previousDataSignatureRef = useRef(dataSignature);
  const yAxisDomain = useMemo(() => {
    if (numericValues != null) {
      return getYAxisDomainForNumericValues(numericValues, { autoMinValue });
    }
    return getYAxisDomain(autoMinValue, undefined, undefined);
  }, [autoMinValue, numericValues]);

  if (previousDataSignatureRef.current !== dataSignature) {
    previousDataSignatureRef.current = dataSignature;
    if (maxYAxisLabelWidth !== 0) {
      setMaxYAxisLabelWidth(0);
    }
    if (calculatedYAxisWidth !== DEFAULT_VALUE_AXIS_WIDTH_PX) {
      setCalculatedYAxisWidth(DEFAULT_VALUE_AXIS_WIDTH_PX);
    }
  }

  useEffect(() => {
    if (maxYAxisLabelWidth > 0) {
      setCalculatedYAxisWidth(Math.max(maxYAxisLabelWidth + 16, 40));
    }
  }, [maxYAxisLabelWidth]);

  const handleYAxisLabelWidthChange = useCallback((width: number) => {
    setMaxYAxisLabelWidth((prev) => Math.max(prev, width));
  }, []);

  return {
    calculatedYAxisWidth,
    yAxisDomain,
    handleYAxisLabelWidthChange,
  };
};

export interface ColumnChartValueYAxisProps {
  yAxisId?: string;
  orientation?: "left" | "right";
  calculatedYAxisWidth: number;
  yAxisDomain: ReturnType<typeof getYAxisDomain>;
  onWidthChange: (width: number) => void;
}

/** Numeric Y axis for column-layout cartesian charts (bar, line, area, composed). */
export const ColumnChartValueYAxis = ({
  yAxisId,
  orientation = "left",
  calculatedYAxisWidth,
  yAxisDomain,
  onWidthChange,
}: ColumnChartValueYAxisProps) => (
  <YAxis
    yAxisId={yAxisId}
    orientation={orientation}
    type="number"
    width={calculatedYAxisWidth}
    tickLine={false}
    axisLine={false}
    allowDecimals
    domain={yAxisDomain as AxisDomain}
    tickFormatter={formatChartValueAxisTick}
    tick={(props) => (
      <MeasurableValueAxisTick
        {...props}
        valueFormatter={formatChartValueAxisTick}
        onWidthChange={onWidthChange}
        textAnchor={orientation === "right" ? "start" : "end"}
      />
    )}
  />
);
